// Unread conversation state shared by the tabs and page loads of one browser, so that each of
// them does not re-read the whole inbox. Only read flags and dates are stored: message previews
// never leave memory.
const STORAGE_KEY = 'uroborox.notifications';
const VERSION = 1;

const getStorage = () => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch (e) {
    return null;
  }
};

const withoutPreviews = activity =>
  Object.entries(activity).reduce((stored, [id, a]) => {
    stored[id] = { unread: !!a?.unread, latestAt: a?.latestAt, group: a?.group, tab: a?.tab };
    return stored;
  }, {});

/**
 * Read the last refresh shared by this browser for the given user.
 *
 * @param {string} userId current user's uuid
 * @param {Storage} [storage]
 * @returns {{ userId: string, fetchedAt: number, activity: Object|null }|null}
 */
export const readSharedNotifications = (userId, storage = getStorage()) => {
  if (!userId || !storage) return null;
  try {
    const entry = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
    if (
      entry?.version !== VERSION ||
      entry.userId !== userId ||
      !Number.isFinite(entry.fetchedAt)
    ) {
      return null;
    }
    const activity =
      entry.activity && typeof entry.activity === 'object' ? withoutPreviews(entry.activity) : null;
    return { userId, fetchedAt: entry.fetchedAt, activity };
  } catch (e) {
    return null;
  }
};

/**
 * Share a refresh with the other tabs and page loads. A refresh without activity (a failure)
 * keeps the last known activity. An older refresh never replaces a newer one.
 *
 * @param {Object} refresh
 * @param {string} refresh.userId
 * @param {number} refresh.fetchedAt time when the refresh started
 * @param {Object} [refresh.activity] conversation activity by transaction uuid
 * @param {Storage} [storage]
 */
export const writeSharedNotifications = (
  { userId, fetchedAt, activity },
  storage = getStorage()
) => {
  if (!userId || !storage || !Number.isFinite(fetchedAt)) return;
  try {
    const previous = readSharedNotifications(userId, storage);
    if (previous && previous.fetchedAt > fetchedAt) return;
    const stored = activity ? withoutPreviews(activity) : previous?.activity || null;
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: VERSION, userId, fetchedAt, activity: stored })
    );
  } catch (e) {
    // Without browser storage every page load refreshes on its own.
  }
};

/**
 * Keep a read receipt from showing the conversation as unread in other tabs or page loads.
 *
 * @param {string} userId
 * @param {string} txId transaction uuid
 * @param {Storage} [storage]
 */
export const markSharedConversationRead = (userId, txId, storage = getStorage()) => {
  const entry = readSharedNotifications(userId, storage);
  if (!entry?.activity?.[txId]?.unread) return;
  writeSharedNotifications(
    {
      ...entry,
      activity: { ...entry.activity, [txId]: { ...entry.activity[txId], unread: false } },
    },
    storage
  );
};

/**
 * Forget the shared state, e.g. on logout.
 *
 * @param {Storage} [storage]
 */
export const clearSharedNotifications = (storage = getStorage()) => {
  try {
    if (storage) storage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to clear.
  }
};
